'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { createSPAClient } from '@/lib/supabase/client';
import { captureClientError } from '@/lib/error-reporting/client';
import { isRedirectError } from 'next/dist/client/components/redirect-error';

/**
 * Handles Supabase email verification links that redirect with #access_token.
 *
 * Supabase's default invite email template uses {{ .ConfirmationURL }} which
 * redirects to SITE_URL/#access_token=xxx (implicit grant flow). This component
 * detects the hash fragment, establishes a session, and routes the user correctly.
 *
 * CRITICAL for invite flow: Without this on the landing page, invited users
 * land on the marketing page with an unusable hash token and get stuck
 * (can't sign in — no password, can't sign up — account exists).
 *
 * Routing:
 * - type=invite → check for pending team invitation → accept-invitation page
 * - type=recovery → /auth/reset-password
 * - type=signup → /onboarding
 * - default → /dashboard
 */
export default function AuthHashRedirect() {
  const router = useRouter();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const hash = window.location.hash;
    if (!hash || !hash.includes('access_token=')) return;

    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const authType = params.get('type');

    if (!accessToken || !refreshToken) return;

    const supabase = createSPAClient();

    const applySession = async () => {
      const { error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (error) {
        throw error;
      }

      // Clean hash from URL immediately
      let url: URL;
      try {
        url = new URL(window.location.href);
      } catch (_error) {
        // intentional: window.location.href should always be valid, but handle defensively
        throw new Error('Failed to parse current URL');
      }
      url.hash = '';
      window.history.replaceState({}, '', url.toString());

      // Handle invite type — find and redirect to pending team invitation
      if (authType === 'invite') {
        try {
          const {
            data: { user },
          } = await supabase.auth.getUser();

          if (user?.email) {
            // Query team_invitations for pending invitation matching this email
            // RLS policy "Anon can view pending invitations" allows this query
            const { data: invitation } = await (supabase
              .from('team_invitations' as 'leads') // Cast: team_invitations not in generated types yet
              .select('token')
              .eq('email', user.email)
              .eq('status', 'pending')
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle() as unknown as Promise<{ data: { token: string } | null }>);

            if (invitation?.token) {
              router.replace(`/auth/team/accept-invitation/${invitation.token}`);
              return;
            }
          }
        } catch (inviteError) {
          // NEXT_REDIRECT must bubble to Next.js — never swallow in client catch blocks (Rule #33)
          if (isRedirectError(inviteError)) throw inviteError;
          // If invite check fails, fall through to default routing
          captureClientError(inviteError, 'client_runtime', {
            component: 'AuthHashRedirect',
            action: 'check_pending_invitation',
          }).catch(() => {});
        }

        // No pending invitation found — redirect to dashboard
        router.replace('/dashboard');
        return;
      }

      // Route based on auth type
      const nextPath =
        authType === 'recovery'
          ? '/auth/reset-password'
          : authType === 'signup'
            ? '/onboarding'
            : '/dashboard';

      router.replace(nextPath);
    };

    applySession().catch((error) => {
      // NEXT_REDIRECT must bubble to Next.js — never swallow in client catch blocks (Rule #33)
      if (isRedirectError(error)) throw error;
      captureClientError(error, 'client_runtime', {
        component: 'AuthHashRedirect',
        action: 'set_session_from_hash',
        authType: authType ?? 'unknown',
      }).catch(() => {});
    });
  }, [router]);

  return null;
}
